// analytics.js
// Central GA4 (gtag) helper for client-side events.
// gtag itself is loaded in public/index.html (measurement id G-EEMT3NPXGV).
// Server-side events (purchase, trial_start, calls, bookings) are NOT sent
// from here — they go through the backend Measurement Protocol helper, because
// at those moments no browser page is open.

import posthog from "posthog-js";

import decodeToken from "./decodeToken";
import { getVariant, EXPERIMENTS } from "./experiments";

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
// console.log(GA_MEASUREMENT_ID)

// Tag every event with debug_mode so it shows up in GA4 Admin > DebugView
// WITHOUT the GA Debugger extension. Fully env-controlled:
//   REACT_APP_GA_DEBUG=true   → debug ON  (localhost / staging testing)
//   REACT_APP_GA_DEBUG=false  → debug OFF (production)
const GA_DEBUG = process.env.REACT_APP_GA_DEBUG === "true";
// console.log("GA_DEBUG", GA_DEBUG, process.env.REACT_APP_GA_DEBUG)
// Treat the Android/iOS WebView wrapper as a separate surface from the browser.
const getAppSurface = () => {
  if (typeof window === "undefined") return "web";
  if (window.ReactNativeWebView) return "android_webview";
  if (window.webkit && /iPhone|iPad|iPod/i.test(navigator.userAgent))
    return "ios_webview";
  return "web";
};
// fdfd
const gtagReady = () =>
  typeof window !== "undefined" && typeof window.gtag === "function";

// True while an admin is impersonating a user (flag set by hooks/useImpersonation.js).
// During impersonation the browser is logged into the USER's account, but every
// action + the location/timezone/device are the ADMIN's. Firing GA events here
// would pollute the real user's journey with the admin's activity — wrong geo
// (e.g. India), and phantom events (onboarding_start on /login) attributed to the
// user. So we hard-stop ALL GA tracking while this flag is set.
const isImpersonating = () => {
  try {
    return (
      typeof window !== "undefined" &&
      localStorage.getItem("isAdminImpersonation") === "true"
    );
  } catch {
    return false;
  }
};
// When debug is on, flip the WHOLE GA4 session into debug mode at app start
// (config-level), so GA4 DebugView shows every event — including the automatic
// ones (page_view, session_start, scroll) and anything fired BEFORE the user
// logs in. Previously debug_mode was only attached once a user was identified
// (setGAUser/ensureGAUser) plus per-event, so pre-login events never reached
// DebugView. The inline gtag stub in index.html defines window.gtag before this
// bundle runs, so this queues safely even before gtag.js finishes loading.
if (GA_DEBUG && gtagReady()) {
  window.gtag("config", GA_MEASUREMENT_ID, { debug_mode: true });
}

// ── PostHog (PHASE 1 — dual-write mirror) ─────────────────────────────
// Every event that goes to GA4 also goes to PostHog. GA4 stays the source of
// truth for now; this exists so both data sets can be compared before anything
// is cut over. Hard-off unless REACT_APP_POSTHOG_ENABLED === "true", so a
// deploy with the flag unset behaves exactly as it did before.
//
// NOTE for reviewers: REACT_APP_* values are compiled into the JS bundle, so
// the phc_ key is publicly visible. That is by design — it is a write-only
// publishable key. The Personal API key (read access) is backend-only and is
// NOT introduced here.
const PH_ENABLED = process.env.REACT_APP_POSTHOG_ENABLED === "true";
const PH_KEY = process.env.REACT_APP_POSTHOG_KEY;
const PH_HOST =
  process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";
// Use the same REACT_APP_ENV deployment label as the rest of the frontend.
// Set REACT_APP_ENV=staging for Dev_Server_main at build time. Do not use
// NODE_ENV: React builds set it to production for staging builds too.
const APP_ENVIRONMENT = process.env.REACT_APP_ENV || "local";

let _phReady = false;
if (PH_ENABLED && PH_KEY && typeof window !== "undefined") {
  try {
    posthog.init(PH_KEY, {
      api_host: PH_HOST,
      // ── Cost controls. Every one of these is ON by default in PostHog and
      // every one of them costs money or corrupts the funnel. Do not flip any
      // of them without re-reading the switchboard in HANDOFF.md.
      //
      // autocapture records EVERY click/change/submit — can multiply event
      // volume 10-50x and blow the free tier on its own.
      autocapture: false,
      // RouteTracker already emits named visit_<screen>_view events. Leaving
      // PostHog's own pageview capture on would count every screen twice.
      capture_pageview: false,
      capture_pageleave: false,
      // Web vitals. ON by default — it was firing $web_vitals on real users
      // within hours of Phase 1 going live, which is both unbilled-for volume
      // and noise: the backend picked $web_vitals as a user's "last step", so
      // the outbound recovery agent would have told them they stopped at
      // "$web Vitals" instead of "Test Call". We have no performance-monitoring
      // use case, so it is off.
      capture_performance: false,
      // Session replay stays with Microsoft Clarity, which is free and
      // unlimited. PostHog charges per recording past 5k/month.
      disable_session_recording: true,
      // Only build person profiles for users who actually identify. Visitors
      // who never sign up cost nothing.
      person_profiles: "identified_only",
    });
    _phReady = true;
  } catch {
    // Blocked, misconfigured, or restricted WebView — analytics never breaks UI.
    _phReady = false;
  }
}

// Impersonation is re-checked here (not just at init) because the flag is set
// at runtime by hooks/useImpersonation.js, long after this module loads.
const phReady = () => _phReady && !isImpersonating();

// Send one event to PostHog. `payload` is the SAME object handed to gtag, so
// the two systems receive identical properties and can be diffed directly.
const phCapture = (name, payload) => {
  if (!phReady() || !name) return;
  try {
    posthog.capture(name, {
      ...payload,
      environment: APP_ENVIRONMENT,
      event_source: "client",
    });
  } catch {
    // never break the UI
  }
};

// ── Identity reset on logout ─────────────────────────────────────────
// PostHog stores identity in a COOKIE as well as localStorage, and every logout
// path in this app only runs localStorage.clear(). So after a logout PostHog
// still believes it is the previous user, and posthog.identify() only merges
// an anonymous visitor into a user when the CURRENT state is anonymous
// (posthog-js: `if (identityDidChange && isKnownAnonymous)`). Before this fix,
// the next person to sign up in the same browser had their pre-signup events
// written onto the PREVIOUS account, and never merged into their own; the
// previous user's "last active" time was also refreshed by someone else's
// browsing, which the dropout-call cron reads.
//
// Handled here, in the one door every event goes through, rather than at the
// 7 separate logout call sites, so a future logout path cannot forget it.
const phIsIdentified = () => {
  try {
    return posthog.get_property("$user_state") === "identified";
  } catch {
    return false;
  }
};

// Bind the person. Safe to call repeatedly — PostHog persists identity in its
// own storage, so unlike gtag this does NOT need re-applying on every event.
const phIdentify = (userId, props = {}) => {
  if (!phReady() || !userId) return;
  try {
    const id = String(userId);
    // A DIFFERENT user is still identified in this browser: start clean first,
    // or this user's events would be written onto theirs. Never reset from an
    // anonymous state; that anonymous id is exactly what identify() merges.
    if (posthog.get_distinct_id() !== id && phIsIdentified()) posthog.reset();
    posthog.identify(id, props);
  } catch {
    // never break the UI
  }
};

// Runs before every captured event (trackEvent) with the logged-in user id, or
// null when nobody is logged in.
const phEnsureUser = (uid) => {
  if (!phReady()) return;
  try {
    if (!uid) {
      // Logged out, but PostHog still holds the previous user's identity (see
      // above). Start a fresh anonymous visitor so this browsing is not
      // attributed to someone else's account.
      if (phIsIdentified()) posthog.reset();
      return;
    }
    if (posthog.get_distinct_id() === String(uid)) return;
    // Also covers a user who logged in BEFORE this build shipped: their browser
    // holds an anonymous PostHog id and identify() was never called for them.
    phIdentify(uid, {
      app_user_id: String(uid),
      app_surface: getAppSurface(),
    });
  } catch {
    // never break the UI
  }
};

// Person-scoped properties — PostHog's equivalent of gtag user_properties.
const phSetPersonProperties = (props = {}) => {
  if (!phReady()) return;
  try {
    posthog.setPersonProperties(props);
  } catch {
    // never break the UI
  }
};

// ── Microsoft Clarity ─────────────────────────────────────────────────

const clarityReady = () =>
  typeof window !== "undefined" && typeof window.clarity === "function";

// _clck = Clarity user id, _clsk = session id. Dono me pehla segment hi chahiye;

const readClarityCookie = (name) => {
  try {
    if (typeof document === "undefined") return null;
    const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    if (!m) return null;
    const v = decodeURIComponent(m[1]);
    return (v.includes("^") ? v.split("^") : v.split("|"))[0] || null;
  } catch {
    return null;
  }
};

export const getClarityIds = () => ({
  clarity_user_id: readClarityCookie("_clck"),
  clarity_session_id: readClarityCookie("_clsk"),
});

// Dono id na mile to param bhejte hi nahi — GA4 me aadhi-adhoori row se
// admin panel galat URL bana lega.
const clarityParams = () => {
  const ids = getClarityIds();
  return ids.clarity_user_id && ids.clarity_session_id ? ids : {};
};

// Clarity docs kehti hain identify HAR page par call karo. Isliye uid + path par
// gate karte hain — ek route par ek hi baar, har event par nahi.
let _clarityBoundKey = null;
const ensureClarityUser = (uid) => {
  // Impersonation me GA ki tarah Clarity ko bhi tag nahi karte, warna admin ki
  // recording user ke naam par chadh jayegi.
  if (!clarityReady() || !uid || isImpersonating()) return;
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const key = `${uid}|${path}`;
  if (key === _clarityBoundKey) return;
  try {
    window.clarity("identify", String(uid));
    _clarityBoundKey = key;
  } catch {
    // Clarity blocked (adblocker / restricted WebView) — analytics kabhi UI na tode.
  }
};

// Current user_id from the stored JWT (so it survives page reloads — not just
// the in-memory login call).
const getCurrentUserId = () => {
  try {
    const token =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("token")
        : null;
    return token ? decodeToken(token)?.id || null : null;
  } catch {
    return null;
  }
};

// Tracks the user_id currently bound to gtag's config so we don't re-config on
// every single event.
let _gaBoundUserId = null;

// Ensure gtag's config carriesjhgj the current user_id BEFORE firing an event.
// gtag attaches the config user_id to every collect request, so this guarantees
// user_id is on every event — even after a reload where index.html re-inits
// gtag without a user_id. Returns the resolved user_id (or null).
const ensureGAUser = () => {
  if (!gtagReady() || isImpersonating()) return null;
  const uid = getCurrentUserId();
  if (uid && String(uid) !== _gaBoundUserId) {
    window.gtag("config", GA_MEASUREMENT_ID, {
      user_id: String(uid),
      app_user_id: String(uid),
      ...(GA_DEBUG ? { debug_mode: true } : {}),
    });
    _gaBoundUserId = String(uid);
  }
  // PostHog needs no per-event re-binding — its identity survives reloads. This
  // call only covers the user who was ALREADY logged in when this build shipped
  // and therefore never went through setGAUser()/identify().
  phEnsureUser(uid);
  return uid;
};

// Call at login. Joins a user's steps across sessions/devices.
export const setGAUser = (userId) => {
  if (!userId || isImpersonating()) return;
  // PHASE 4: identify PostHog FIRST and unconditionally. If this stayed behind
  // the gtag check, an ad-blocked user would never be identified — every event
  // they fire would pile up on an anonymous person and the recovery-call cron
  // would never find them.
  phIdentify(userId, {
    app_user_id: String(userId),
    app_surface: getAppSurface(),
    go_live_flow_variant: getVariant(EXPERIMENTS.GO_LIVE_FLOW),
  });
  if (!gtagReady()) return;
  window.gtag("config", GA_MEASUREMENT_ID, {
    user_id: String(userId),
    app_user_id: String(userId),
    ...(GA_DEBUG ? { debug_mode: true } : {}),
  });
  _gaBoundUserId = String(userId);
  window.gtag("set", "user_properties", {
    app_user_id: String(userId),
    app_surface: getAppSurface(),
    // A/B: GA4 user property YAHIN set hoti hai — login pe, user identify hote
    // hi. GA4 user property sirf uske BAAD wale events pe lagti hai, isliye baad
    // me set karte to shuruaati funnel events bina variant ke chale jaate aur
    // A-vs-B comparison adhoora reh jaata.
    go_live_flow_variant: getVariant(EXPERIMENTS.GO_LIVE_FLOW),
  });
  // Login ke waqt hi Clarity ki recording par bhi user ka tag laga do.
  ensureClarityUser(userId);
};

// Update user-scoped properties as account state changes
// (e.g. account_type: free_test -> trial -> paid, plan, agent_type...).
export const setUserProperties = (props = {}) => {
  if (isImpersonating()) return;
  // PostHog unconditionally — see setGAUser for why this is not behind gtag.
  phSetPersonProperties(props);
  if (gtagReady()) window.gtag("set", "user_properties", props);
};

// ── Auth Key events (sign_up / login) — surface-agnostic ──────────────
// Fire sign_up / login EXACTLY ONCE per authentication, keyed by the JWT, so
// it works no matter HOW the token reaches the app:
//   • web:  ?token=… in the URL after the /auth/google redirect
//   • email/OTP: token set on verify
//   • APP (React Native): the mobile login callback redirects to a
//     com.rexptin://signup?token=… DEEP LINK, and the native shell may hand
//     the JWT to the WebView via localStorage instead of a ?token= URL — the
//     old URL-only path missed sign_up/login entirely on the app.
// Dedup is by TOKEN VALUE: every real login issues a new JWT → fires once;
// page re-mounts / app reopens with the SAME token don't re-fire. Every caller
// should route sign_up/login through here instead of trackEvent directly.
const AUTH_RECORDED_KEY = "ga4_auth_recorded";

// Returns TRUE once this token's auth event has actually gone out.
export const fireAuthEvent = ({ isExisting, method, token } = {}) => {
  let t = token || null;
  try {
    t =
      t ||
      (typeof localStorage !== "undefined"
        ? localStorage.getItem("token")
        : null);
    if (!t) return false;
    if (
      typeof localStorage !== "undefined" &&
      localStorage.getItem(AUTH_RECORDED_KEY) === t
    ) {
      return true; // this login already recorded
    }
  } catch {
    // localStorage blocked (private mode / restricted WebView) — still fire.
  }

  const sent = trackEvent(isExisting ? "login" : "sign_up", { method });

  // Mark as recorded ONLY after the event really left the browser.
  //
  // This used to mark FIRST and fire second. trackEvent silently no-ops when
  // gtag isn't ready yet (gtag.js is an async third-party script — much more
  // likely to be late inside the native WebView, and auth events fire earliest
  // in the session). So the very first attempt marked the token as "done" while
  // sending nothing, and every retry then short-circuited on that marker —
  // sign_up / login were lost permanently for that login, even though every
  // later event worked fine.
  if (sent) {
    try {
      if (typeof localStorage !== "undefined")
        localStorage.setItem(AUTH_RECORDED_KEY, t);
    } catch {
      /* storage blocked — caller may retry, trackEvent's own guard dedupes */
    }
  }
  return sent;
};

// True once the current localStorage token's auth event has been recorded.
// Lets a surface (e.g. the app deep-link fallback) skip an extra getUserDetails
// call when the login was already logged.
export const isAuthEventRecorded = () => {
  try {
    if (typeof localStorage === "undefined") return false;
    const t = localStorage.getItem("token");
    return !!t && localStorage.getItem(AUTH_RECORDED_KEY) === t;
  } catch {
    return false;
  }
};

// Fire a custom funnel event. app_surface is attached to every event so the
// app-vs-web split works even before user_properties propagate.
// Returns TRUE only if the event was actually handed to gtag. Callers that
// remember "already sent" (see fireAuthEvent) MUST check this — otherwise they
// mark an event done that never left the browser, and it is lost forever.
export const trackEvent = (name, params = {}) => {
  // console.log("Tracking event", name, params);
  if (!name) return false;
  // Admin impersonation → don't attribute the admin's actions to the user.
  if (isImpersonating()) return false;
  // PHASE 4: PostHog is NO LONGER gated behind gtag.
  //
  // This used to read `if (!gtagReady()) return false`, so an ad-blocker that
  // killed gtag also silently killed PostHog — those users produced no events
  // at all, and the recovery-call cron would never see them. That was the
  // correct trade during the parallel run (both systems had to see an identical
  // event set for the comparison to mean anything), but PostHog is the system
  // of record now, and inheriting GA4's blind spot would be the whole point of
  // the migration thrown away.
  //
  // Bail only when NEITHER destination can accept the event.
  if (!gtagReady() && !phReady()) return false;
  // One-shot guard: this event already went out for this user on this
  // browser → don't hit GA again. Keyed by NAME only, so changed params
  // don't sneak a second hit of the same funnel step through.
  if (alreadySent(name)) return false;
  // Read the id directly rather than via ensureGAUser(), which returns null
  // when gtag is unavailable — the event still needs the user stamped on it for
  // PostHog even when gtag is blocked.
  const uid = getCurrentUserId();
  // Guarantee user_id is bound to gtag's config so it rides on this event.
  if (gtagReady()) ensureGAUser();
  // PostHog persists identity itself; this only covers a user who logged in
  // before this build shipped and so never went through identify().
  phEnsureUser(uid);
  // Recording par user ka tag, aur Clarity ki session id event ke saath —
  // dono isi jagah se har event par jaate hain.
  ensureClarityUser(uid);
  // Built once and handed to BOTH systems, so GA4 and PostHog receive byte-for-
  // byte identical properties. That is what makes the parallel-run comparison
  // meaningful — any difference in the numbers is a real difference.
  const payload = {
    app_surface: getAppSurface(),
    ...clarityParams(),
    ...(uid ? { user_id: String(uid), app_user_id: String(uid) } : {}),
    ...(GA_DEBUG ? { debug_mode: true } : {}),
    ...params,
  };
  // GA4 is now Ads-only, and may legitimately be absent (ad-blocker, or the day
  // GA4 is finally switched off). PostHog is sent to unconditionally.
  if (gtagReady()) window.gtag("event", name, payload);
  phCapture(name, payload);
  return true;
};

// ── One-shot guard (applies to EVERY event) ───────────────────────────
// Each event fires at most ONCE per user per browser — keyed by event NAME
// only. Re-visiting a page, a re-render (StrictMode double mount) or a full
// reload will NOT re-send it. Sent events are remembered in localStorage
// keyed by name + user id (pre-login events use "anon"), so the guard
// survives reloads and a different user logging in on the same browser still
// gets their own events.
//
// The key used to include the PARAMS too. That meant "same event, slightly
// different values" counted as a NEW event: the user edits a field, presses
// Continue again, and the same funnel step lands in GA twice. Params are out
// of the key now, so one name = one hit per user, which is what the funnel
// is supposed to measure.
//
// Consequence: an event whose whole POINT is to repeat with different values
// must be listed in ONCE_GUARD_EXEMPT below, or only its first occurrence is
// ever recorded.
// ⚠️ DO NOT RENAME THIS PREFIX — not even after GA4 is gone and the "ga4_"
// looks like dead legacy. These keys are the record of which funnel events a
// user has ALREADY fired, and they live in the user's own browser. Change the
// prefix and every existing user's record instantly reads as empty, so every
// funnel event fires a second time — a one-off flood of phantom conversions
// across GA4, PostHog and anything downstream of them. The name is permanent;
// the cost of it being slightly wrong is zero.
const SENT_KEY_PREFIX = "ga4_sent_";

// Events that must fire EVERY time (exempt from the one-shot guard):
// - sign_up / login: auth Key events — logging in again on the same browser
//   is a real, countable login, not a duplicate.
// - purchase: one per transaction. Dropping a repeat purchase would silently
//   lose revenue data.
// - screen_view: one generic name reused for EVERY screen (screen_name is a
//   param), so a name-only guard would record just the first screen.
const ONCE_GUARD_EXEMPT = new Set([
  "sign_up",
  "login",
  "purchase",
  "screen_view",
]);

const alreadySent = (name) => {
  try {
    if (ONCE_GUARD_EXEMPT.has(name)) return false; // always allowed through
    if (typeof localStorage === "undefined") return false;
    const key = `${SENT_KEY_PREFIX}${getCurrentUserId() || "anon"}_${name}`;
    if (localStorage.getItem(key)) return true; // already sent for this user
    localStorage.setItem(key, "1");
    return false;
  } catch {
    // localStorage unavailable (private mode) — can't dedupe, send normally.
    return false;
  }
};

// Cheap fallback funnel — send on every route change.
export const trackScreen = (screen_name, params = {}) => {
  if (!screen_name) return;
  trackEvent("screen_view", { screen_name, ...params });
};

// ── First-purchase (client-side) ──────────────────────────────────────
// The FIRST purchase fires from the browser, on the post-checkout thank-you
// landing. Server-side `purchase` (Measurement Protocol) is left to renewals
// only — see backend stripeModel.js. This split avoids double-counting the
// initial payment in GA4.
//
// Flow: RecommendedPlan stashes the plan/value just before redirecting to
// Stripe; the thank-you landing (PaymentSuccess / GoLiveWizard) calls
// firePendingPurchase() on mount. It fires once — the stash is cleared as
// soon as the event goes out, so a page refresh can't re-fire it.
const PENDING_PURCHASE_KEY = "ga4_pending_purchase";

// Save the details we already know at checkout time so the landing page can
// build the purchase event even after a full reload back from Stripe.
export const stashPendingPurchase = (data = {}) => {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(data));
  } catch {
    // localStorage unavailable (private mode etc.) — purchase just won't fire
    // client-side; not worth breaking checkout over.
  }
};

// Fire the stashed purchase exactly once, then clear it. Safe to call from
// several mount points (PaymentSuccess + GoLiveWizard) — whichever runs first
// clears the stash so the others no-op.
export const firePendingPurchase = () => {
  try {
    if (typeof localStorage === "undefined") return;
    const raw = localStorage.getItem(PENDING_PURCHASE_KEY);
    if (!raw) return;
    // Clear BEFORE firing so a re-render / double-mount can't double-send.
    localStorage.removeItem(PENDING_PURCHASE_KEY);
    const data = JSON.parse(raw);
    // Mirror the user-scoped state the server used to set on first purchase:
    // this user is now paying, on this plan tier. plan slug = lowercased name.
    const uid = getCurrentUserId();
    setUserProperties({
      account_type: "paid",
      plan: data.plan ? String(data.plan).toLowerCase() : undefined,
      ...(uid ? { app_user_id: String(uid) } : {}),
    });
    // trackEvent already stamps user_id + app_user_id into params.
    trackEvent("purchase", {
      transaction_id: data.transaction_id,
      value: data.value,
      currency: data.currency,
      plan: data.plan,
      billing_cycle: data.billing_cycle,
    });
  } catch {
    // Bad JSON or gtag not ready — swallow; analytics must never break the UI.
  }
};

// ── Onboarding Phone Lookup Event Helpers ────────────────────────────
export const trackPhoneLookupSuccess = (params = {}) =>
  trackEvent("business_phone_lookup_success", params);
export const trackPhoneLookupNotFound = (phone) =>
  trackEvent("business_phone_lookup_not_found", { phone });
export const trackNotOnGoogleMapsClick = () =>
  trackEvent("im_not_on_google_maps_click", { screen: "phone_lookup" });
export const trackBusinessPhoneConfirmed = (params = {}) =>
  trackEvent("business_phone_confirmed", params);

export { getAppSurface };
